import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  TabsHeader,
  Tab,
  TabsBody,
  TabPanel,
} from '@material-tailwind/react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  UserGroupIcon,
  UserIcon,
  ShareIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';

import { listReports, downloadReport, getShare, getReportPreview } from '../../services/reportApi';
import ShareDialog from '../../components/ShareDialog';
import { friendlyTimeFormatDt } from '../../utils';
import { UserAuth } from '../../context/AuthContext';

// Format active engagement time
function formatActiveTime(seconds) {
  if (!seconds || seconds === 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

// Get engagement level based on active time
function getEngagementColor(seconds) {
  if (!seconds || seconds < 60) return 'text-gray-400';
  if (seconds < 180) return 'text-blue-500';
  return 'text-green-600';
}

function ReportsListPage() {
  const { user } = UserAuth();
  const [ownReports, setOwnReports] = useState([]);
  const [teamReports, setTeamReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [activeTab, setActiveTab] = useState('own');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [shareData, setShareData] = useState({});
  const navigate = useNavigate();

  const handleShare = useCallback((report) => {
    setSelectedReport(report);
    setShareDialogOpen(true);
  }, []);

  // Fetch share analytics only for reports that have a shareId
  const loadShareData = useCallback(async (reports) => {
    const reportsWithShares = reports.filter((r) => r.shareId);
    if (reportsWithShares.length === 0) return;

    const shareInfo = {};
    await Promise.all(
      reportsWithShares.map(async (report) => {
        const reportId = report.reportId || report.id;
        try {
          const data = await getShare(reportId);
          if (data.share) {
            shareInfo[reportId] = data.share;
          }
        } catch (err) {
          // Silently fail for individual share fetches
        }
      })
    );
    setShareData((prev) => ({ ...prev, ...shareInfo }));
  }, []);

  const loadReports = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await listReports();

      let ownList = [];
      let teamList = [];

      // Handle { own: [...], team: [...] } response format
      if (data && typeof data === 'object') {
        ownList = Array.isArray(data.own) ? data.own : [];
        teamList = Array.isArray(data.team) ? data.team : [];
      } else if (Array.isArray(data)) {
        // Fallback for flat array response
        ownList = data;
      } else {
        console.warn('Unexpected reports response format:', data);
      }

      setOwnReports(ownList);
      setTeamReports(teamList);

      // Load share data for all reports
      const combinedReports = [...ownList, ...teamList];
      if (combinedReports.length > 0) {
        loadShareData(combinedReports);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError(err.message);
      setOwnReports([]);
      setTeamReports([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadShareData]);

  useEffect(() => {
    if (user?.uid) {
      loadReports();
    }
  }, [user?.uid, loadReports]);

  const handleDownload = useCallback(async (reportId) => {
    try {
      setDownloadingId(reportId);
      const { downloadUrl } = await downloadReport(reportId);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const ActionsCellRenderer = useCallback((params) => {
    const report = params.data;
    const reportId = report.reportId || report.id;
    return (
      <div className="flex items-center gap-1">
        {report.shareUrl && (
          <Tooltip content="Preview">
            <IconButton
              variant="text"
              size="sm"
              onClick={async () => {
                try {
                  const html = await getReportPreview(reportId);
                  const blob = new Blob([html], { type: 'text/html' });
                  window.open(URL.createObjectURL(blob), '_blank');
                } catch (err) {
                  console.error('Preview error:', err);
                  setError(err.message);
                }
              }}
            >
              <EyeIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip content="Share">
          <IconButton
            variant="text"
            size="sm"
            onClick={() => handleShare(report)}
          >
            <ShareIcon className="h-4 w-4" />
          </IconButton>
        </Tooltip>
        <Tooltip content="Download PDF">
          <IconButton
            variant="text"
            size="sm"
            onClick={() => handleDownload(reportId)}
            disabled={downloadingId === reportId}
          >
            {downloadingId === reportId ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
          </IconButton>
        </Tooltip>
      </div>
    );
  }, [downloadingId, handleDownload, handleShare, setError]);

  const OpensCellRenderer = useCallback((params) => {
    const report = params.data;
    const reportId = report.reportId || report.id;
    const share = shareData[reportId];
    if (!share) return <span className="text-gray-400">-</span>;
    const viewCount = share.analytics?.viewCount || 0;
    const activeTime = share.analytics?.totalActiveSeconds;
    if (viewCount === 0) return <span className="text-gray-400">Not opened</span>;
    return (
      <div>
        <div className="flex items-center gap-1">
          <EyeIcon className="h-4 w-4 text-green-500" />
          <span className="font-medium text-green-600 text-sm">
            {viewCount} {viewCount === 1 ? 'open' : 'opens'}
          </span>
        </div>
        {activeTime > 0 && (
          <span className={`text-sm ${getEngagementColor(activeTime)}`}>
            {formatActiveTime(activeTime)} active
          </span>
        )}
      </div>
    );
  }, [shareData]);

  const reportsColDefs = useMemo(() => [
    {
      field: 'reportType',
      headerName: 'Type',
      width: 130,
      filter: 'agTextColumnFilter',
      valueGetter: (p) => p.data.reportType === 'marketing_update' ? 'Marketing' : 'Valuation',
      cellRenderer: (p) => {
        const isMarketing = p.data.reportType === 'marketing_update';
        return (
          <Chip
            size="sm"
            value={isMarketing ? 'Marketing' : 'Valuation'}
            color={isMarketing ? 'purple' : 'blue'}
            variant="ghost"
            className="text-xs py-0.5 px-2"
          />
        );
      },
    },
    {
      headerName: 'Reg',
      filter: 'agTextColumnFilter',
      width: 100,
      valueGetter: (p) => p.data.subjectAircraft?.registration || p.data.aircraftId || '-',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      filter: 'agTextColumnFilter',
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.data.customerName || '-',
    },
    {
      headerName: 'Aircraft',
      filter: 'agTextColumnFilter',
      flex: 1.2,
      minWidth: 180,
      valueGetter: (p) => {
        const sa = p.data.subjectAircraft;
        return sa
          ? `${sa.year || ''} ${sa.manufacturer || ''} ${sa.model || ''}`.trim() || '-'
          : p.data.aircraftModel || '-';
      },
    },
    {
      headerName: 'Preparer',
      filter: 'agTextColumnFilter',
      width: 130,
      valueGetter: (p) => p.data.preparer?.name || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      filter: 'agDateColumnFilter',
      width: 170,
      valueFormatter: (p) => formatDate(p.value),
      comparator: (a, b) => new Date(a || 0) - new Date(b || 0),
    },
    {
      headerName: 'Opens',
      width: 120,
      sortable: true,
      filter: false,
      valueGetter: (p) => {
        const reportId = p.data.reportId || p.data.id;
        return shareData[reportId]?.analytics?.viewCount || 0;
      },
      cellRenderer: OpensCellRenderer,
    },
    {
      headerName: 'Last Opened',
      width: 140,
      filter: false,
      valueGetter: (p) => {
        const reportId = p.data.reportId || p.data.id;
        return shareData[reportId]?.analytics?.lastViewedAt || null;
      },
      valueFormatter: (p) => p.value ? friendlyTimeFormatDt(p.value) : '-',
    },
    {
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filter: false,
      cellRenderer: ActionsCellRenderer,
    },
  ], [shareData, ActionsCellRenderer, OpensCellRenderer, formatDate]);

  const ReportsTable = ({ reports }) => {
    if (reports.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Typography>No reports found</Typography>
          <Button
            variant="text"
            className="mt-2"
            onClick={() => navigate('/dashboard/report-editor')}
          >
            Create your first report
          </Button>
        </div>
      );
    }

    return (
      <div className="ag-theme-quartz" style={{ height: Math.min(reports.length * 48 + 52, 600) }}>
        <AgGridReact
          rowData={reports}
          columnDefs={reportsColDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            suppressMovable: true,
          }}
          getRowId={(params) => params.data.reportId || params.data.id}
          domLayout="normal"
          rowHeight={48}
          headerHeight={44}
          pagination={reports.length > 50}
          paginationPageSize={50}
        />
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h4" color="blue-gray">
          Reports
        </Typography>
        <div className="flex gap-2">
          <Tooltip content="Refresh">
            <IconButton variant="outlined" onClick={loadReports} disabled={isLoading}>
              <ArrowPathIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          <Button
            className="flex items-center gap-2"
            onClick={() => navigate('/dashboard/report-editor')}
          >
            <DocumentPlusIcon className="h-5 w-5" />
            New Report
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <Tabs value={activeTab} onChange={(val) => setActiveTab(val)}>
              <TabsHeader className="bg-gray-100 p-1 m-4 mb-0">
                <Tab value="own" className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  My Reports
                  {ownReports.length > 0 && (
                    <Chip size="sm" value={ownReports.length} className="ml-1" />
                  )}
                </Tab>
                <Tab value="team" className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4" />
                  Team Reports
                  {teamReports.length > 0 && (
                    <Chip size="sm" value={teamReports.length} className="ml-1" />
                  )}
                </Tab>
              </TabsHeader>
              <TabsBody>
                <TabPanel value="own" className="p-0">
                  <ReportsTable reports={ownReports} />
                </TabPanel>
                <TabPanel value="team" className="p-0">
                  <ReportsTable reports={teamReports} />
                </TabPanel>
              </TabsBody>
            </Tabs>
          )}
        </CardBody>
      </Card>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          // Refresh share data for this report
          if (selectedReport) {
            const reportId = selectedReport.reportId || selectedReport.id;
            getShare(reportId)
              .then((data) => {
                if (data.share) {
                  setShareData((prev) => ({ ...prev, [reportId]: data.share }));
                }
              })
              .catch(() => {});
          }
          setSelectedReport(null);
        }}
        reportId={selectedReport?.reportId || selectedReport?.id}
        reportTitle={`${selectedReport?.customerName || ''} - ${selectedReport?.aircraftModel || ''}`}
        reportType={selectedReport?.reportType}
      />
    </div>
  );
}

export default ReportsListPage;
